import {useCallback, useMemo, useRef, useState} from 'react';
import {view} from '@sail/ui';
import {defineMessages, useIntl} from 'react-intl';
import {ChatButtonOptions} from '../../Chat/Row/ChatButtonOptions';
import {TextRow} from '../../Chat/Row/TextRow';
import type {Option} from '../../Chat/Row/ChatButtonOption';
import {useAnalytics} from '../../AnalyticsProvider';
import {
  SendUsAnEmailForm,
  type SendUsAnEmailFormValues,
} from '../../SendUsAnEmail/SendUsAnEmailForm';
import type {MessagePartComponentProps} from '../MessagePartComponentContext';
import {useMessagePartHandlers} from '../MessagePartComponentProvider';

const EMAIL_OPTION_ID = 'channel_email';

const messages = defineMessages({
  emailInstructions: {
    id: 'conversations_ui.follow_up_case_options.email_instructions',
    description:
      'Instructions shown above the follow-up-case email form, before the user writes their message. Matches the legacy inline-data copy.',
    defaultMessage:
      'Sure. Please provide as much detail as possible and an agent will try to get back to you by email within 24 hours.',
  },
  selectionError: {
    id: 'conversations_ui.follow_up_case_options.selection_error',
    description: 'Error shown when selecting a follow-up-case option fails',
    defaultMessage:
      'There was an issue selecting your option. Please check your internet connection and try again.',
  },
});

type FollowUpCaseOption = {
  id: string;
  message: string;
};

type FollowUpCaseOptionsContent = {
  text: string;
  options: FollowUpCaseOption[];
  show_cc_emails_field?: boolean;
  existing_cc_emails?: string[];
  is_email_form_submitted?: boolean;
};

type FollowUpCaseOptionsSubmissionContent = {
  text: string;
  channel: string;
  email_form?: {
    email: string;
    body: string;
    subject?: string;
    cc_emails?: string[];
    attachment_ids?: string[];
  };
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asFollowUpCaseOptionsContent(
  value: unknown,
): FollowUpCaseOptionsContent {
  return value as FollowUpCaseOptionsContent;
}

function buildEmailSubmissionText(values: SendUsAnEmailFormValues): string {
  const lines: string[] = [];
  if (values.subject) {
    lines.push(values.subject);
  }
  if (values.ccEmails && values.ccEmails.length > 0) {
    lines.push(`CC: ${values.ccEmails.join(', ')}`);
  }
  lines.push(values.question ?? '');
  return lines.join('\n');
}

function submissionSucceeded(response: unknown): boolean {
  const record = asRecord(response);
  const code = record?.code;
  return typeof code === 'number' && code < 400;
}

function stringField(
  data: Record<string, unknown> | undefined,
  key: string,
  fallback: string,
): string {
  const value = data?.[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function numberField(
  data: Record<string, unknown> | undefined,
  key: string,
  fallback: number,
): number {
  const value = data?.[key];
  return typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

export function FollowUpCaseOptionsPartComponent({
  conversation,
  message,
  part,
  animated,
  disableInteraction,
}: MessagePartComponentProps) {
  const {onSubmit} = useMessagePartHandlers();
  const analytics = useAnalytics();
  const intl = useIntl();

  const content = useMemo(
    () => asFollowUpCaseOptionsContent(part.content),
    [part.content],
  );

  const [selectedOptionId, setSelectedOptionId] = useState<
    string | undefined
  >();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(
    Boolean(content.is_email_form_submitted),
  );
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [selectionError, setSelectionError] = useState<string | undefined>();

  const messageData = asRecord(message.data);
  const sourceMessageId = stringField(
    messageData,
    'sourceMessageId',
    message.id,
  );
  const sourcePartId = numberField(messageData, 'sourcePartId', part.id);
  const sourceCreatorPartId = `${sourceMessageId}.${sourcePartId}`;

  const isLatestMessage = conversation.mostRecentMessage()?.id === message.id;
  const isLive =
    part.dynamic === 'active' ||
    (part.dynamic === 'if_latest' && isLatestMessage);
  const isDone = !isLive || !!disableInteraction || submitted;
  const isEmailFormOpen = !isDone && selectedOptionId === EMAIL_OPTION_ID;
  const emailInstructions = intl.formatMessage(messages.emailInstructions);

  const submitOutcome = useCallback(
    async (submissionContent: FollowUpCaseOptionsSubmissionContent) => {
      const submissionPart = {
        type: 'follow_up_case_options_submission' as const,
        content: submissionContent,
      };
      if (onSubmit) {
        return onSubmit({
          conversationId: conversation.id,
          submission: {
            id: `${sourceCreatorPartId}.submission`,
            creatorPartId: sourceCreatorPartId,
            parts: [{id: 1, ...submissionPart}],
          },
        });
      }
      return conversation.sendMessage(
        conversation.buildTextMessage(submissionContent.text, {
          creator_part_id: sourceCreatorPartId,
          message_part_submission: submissionPart,
        }),
      );
    },
    [conversation, onSubmit, sourceCreatorPartId],
  );

  const showSelectionFailure = useCallback(() => {
    setSelectedOptionId(undefined);
    setSelectionError(intl.formatMessage(messages.selectionError));
  }, [intl]);

  const handleSelectOption = useCallback(
    async (optionId: string, optionMessage: string) => {
      if (submitting) {
        return;
      }
      analytics.action('uh_merchant_follow_up_case_option_selected', {
        followUpCaseOption: optionId,
      });
      setSelectionError(undefined);
      setSelectedOptionId(optionId);
      if (optionId === EMAIL_OPTION_ID) {
        return;
      }
      setSubmitting(true);
      try {
        const response = await submitOutcome({
          text: optionMessage,
          channel: optionId,
        });
        if (submissionSucceeded(response)) {
          setSubmitted(true);
          return;
        }
      } catch {
        // Fall through to showSelectionFailure below.
      } finally {
        setSubmitting(false);
      }
      showSelectionFailure();
    },
    [analytics, submitting, submitOutcome, showSelectionFailure],
  );

  const handleEmailSubmit = useCallback(
    async (values: SendUsAnEmailFormValues) => {
      setIsSubmissionError(false);
      setSubmitting(true);
      analytics.action('uh_merchant_follow_up_case_email_submit');
      let response: unknown;
      try {
        response = await submitOutcome({
          text: buildEmailSubmissionText(values),
          channel: EMAIL_OPTION_ID,
          email_form: {
            email: values.unauthenticatedEmail ?? '',
            body: values.question ?? '',
            subject: values.subject,
            cc_emails: values.ccEmails,
            attachment_ids: values.attachments?.map((a) => a.id) ?? [],
          },
        });
      } catch {
        setIsSubmissionError(true);
        return;
      } finally {
        setSubmitting(false);
      }
      if (submissionSucceeded(response)) {
        setSubmitted(true);
        return;
      }
      setIsSubmissionError(true);
    },
    [analytics, submitOutcome],
  );

  const handleCancelForm = useCallback(() => {
    analytics.action('uh_merchant_follow_up_case_cancel_form');
    setSelectedOptionId(undefined);
  }, [analytics]);

  const options: Option[] = useMemo(
    () =>
      (content.options ?? []).map((option) => ({
        value: option.id,
        label: option.message,
        onPress: () => handleSelectOption(option.id, option.message),
      })),
    [content.options, handleSelectOption],
  );

  const initialQuestionRef = useRef(
    typeof conversation.context.latestQuestion === 'string'
      ? conversation.context.latestQuestion
      : undefined,
  );

  if (isDone) {
    return null;
  }

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <ChatButtonOptions
        options={options}
        selectedOptionId={selectedOptionId}
        disabled={submitting}
        error={selectionError}
        animated={animated}
        conversation={conversation}
      />
      {isEmailFormOpen ? (
        <>
          {/* eslint-disable-next-line react/style-prop-object */}
          <TextRow style="info" body={emailInstructions} />
          <view.div
            data-testid="follow-up-case-options-email-panel"
            css={{
              stack: 'y',
              gap: 'medium',
              border: '1px solid',
              borderColor: 'border',
              borderRadius: 'medium',
              padding: 'medium',
            }}
          >
            <SendUsAnEmailForm
              showSubjectField={false}
              showUnauthenticatedEmailField={false}
              showCcEmailsField={content.show_cc_emails_field}
              existingCcEmails={content.existing_cc_emails}
              initialQuestionValue={initialQuestionRef.current}
              handleSubmit={handleEmailSubmit}
              onCancel={handleCancelForm}
              isSubmissionPending={submitting}
              isSubmissionError={isSubmissionError}
            />
          </view.div>
        </>
      ) : null}
    </view.div>
  );
}
